import { ApolloError } from '@apollo/client'
import { datadogLogs } from '@datadog/browser-logs'
import { SelectOption } from '@focaldata/cin-ui-components'
import orderBy from 'lodash/orderBy'
import { matchPath } from 'react-router-dom'
import uuid from 'uuid-random'
import {
  Country,
  CustomOptionValidationError
} from '../../data/gql-gen/fieldwork/graphql'
import { LoggerErrorType } from '../../data/logger'
import { CustomCriterionValidationErrors } from '../../data/model/fieldwork'
import {
  DraftQuestionnaireEntry,
  EntryType,
  QuestionSettingCode,
  SettingValue
} from '../../data/model/questionnaire'
import { getQuestionEnabledSettings } from '../questionnaireUtils'

export type CountryOption = Pick<SelectOption, 'id' | 'name'> & {
  countryCode: string
  value: string
}

export const reorderCountriesDropDown = (
  countries: ReadonlyArray<Country>
): CountryOption[] => {
  return orderBy(
    countries.map((country, index, countries) => {
      const hasMultipleLanguages =
        countries.filter(
          ({ countryCode }) => countryCode === country.countryCode
        ).length > 1
      const name = hasMultipleLanguages
        ? `${country.name} - ${country.locale.language}`
        : country.name
      const option: CountryOption = {
        id: `${country.countryCode}-${index}`,
        name,
        countryCode: country.countryCode,
        value: `${country.countryCode}-${country.locale.language}`
      }
      return option
    }),
    ['name'],
    ['asc']
  )
}

// Collapse the per-locale getCountriesV2 rows into a single option per
// country. A multi-language country (e.g. Belgium) is returned once per locale
// (nl-BE, fr-BE); here it becomes one "Belgium" row. The language is chosen
// separately (the LanguageAllocations editor), so the option value is just the
// countryCode — not the legacy "CC-lang" of reorderCountriesDropDown. Ordered
// alphabetically by name. Shared by the single-market CountryPicker (flag-on
// path) and the multi-market Add Market dialog.
export const dedupeCountriesToCountryOptions = (
  countries: ReadonlyArray<Country>
): CountryOption[] => {
  const seen = new Set<string>()
  const unique: CountryOption[] = []
  countries.forEach((country) => {
    if (seen.has(country.countryCode)) return
    seen.add(country.countryCode)
    unique.push({
      id: country.countryCode,
      name: country.name,
      countryCode: country.countryCode,
      value: country.countryCode
    })
  })
  return unique.sort((a, b) => a.name.localeCompare(b.name))
}

export const suggestedCountryOption = (
  options: SelectOption[] | undefined
): SelectOption[] => {
  return orderBy(
    options?.filter(
      (option) => option.countryCode === 'US' || option.countryCode === 'GB'
    ),
    ['name'],
    ['desc']
  )
}

export const percentToProportion: (
  percent: number,
  precision?: number
) => number = (percent, precision) => {
  if (precision === undefined) {
    // Original implementation for backward compatibility
    return Math.round((percent + Number.EPSILON) * 10) / 1000
  }
  const multiplier = Math.pow(10, precision)
  return Math.round((percent / 100 + Number.EPSILON) * multiplier) / multiplier
}

export const proportionToPercent: (
  proportion: number,
  precision?: number
) => number = (proportion, precision) => {
  if (precision === undefined) {
    // Original implementation for backward compatibility
    return Math.round((proportion + Number.EPSILON) * 1000) / 10
  }
  const multiplier = Math.pow(10, precision + 2) // +2 because percent is 100x proportion
  return (
    Math.round((proportion + Number.EPSILON) * multiplier) /
    Math.pow(10, precision)
  )
}

export const calculateTotalCost = (
  cpi: number,
  numOfwantedCompletes: number
): number => {
  const totalCost =
    Math.round((cpi * numOfwantedCompletes + Number.EPSILON) * 100) / 100

  return totalCost
}

export const formatThousandsWithCommas: (cost: number) => string = (cost) => {
  return cost.toLocaleString()
}

export const getSettingValue = (bool: boolean) => {
  if (bool) {
    return SettingValue.Enabled
  }
  return SettingValue.Disabled
}

export const getSettingBool: (
  entry: DraftQuestionnaireEntry | undefined,
  questionSettingCode: QuestionSettingCode
) => boolean = (entry, questionSettingCode) => {
  if (
    entry &&
    (entry.entryType === EntryType.QuestionEntryType ||
      entry.entryType === EntryType.MatrixEntryType)
  ) {
    const enabledSettings = getQuestionEnabledSettings(
      entry.entryItem.settingValues
    )
    return enabledSettings.has(questionSettingCode)
  }
  return false
}

/**
 * Get the direct backend URL for a service, bypassing the proxy.
 * This is needed for links that open in new tabs/windows.
 * @throws Error if the required environment variable is not set
 */
const getDirectBackendUrl = (service: 'respondent'): string => {
  const domain = process.env.REACT_APP_RESPONDENT_DOMAIN

  if (!domain) {
    throw new Error(
      'REACT_APP_RESPONDENT_DOMAIN environment variable is not set'
    )
  }

  // If using proxy mode (URLs with /proxy/ path), construct direct backend URL
  if (domain.includes('/proxy/')) {
    const useProdApis = process.env.REACT_APP_USE_PROD_APIS === 'true'
    const urlExtension = useProdApis ? '.com' : '.dev'

    switch (service) {
      case 'respondent':
        return `https://community.focaldata${urlExtension}`
      default:
        return domain
    }
  }

  // Otherwise return the domain as-is (direct URL, deployed environment, or local service like localhost:8000)
  return domain
}

export const createPreviewUrl = (surveyId: string, panel: string) => {
  const domain = getDirectBackendUrl('respondent')
  const rid = uuid()
  return `${domain}/info?rid=${rid}&preview=true&sid=${surveyId}&ps=${panel}&type=survey`
}

export const handleShowPreview = (surveyId: string, panel: string) => {
  const previewUrl = createPreviewUrl(surveyId, panel)
  window.open(previewUrl, '_blank')
}

const checkIfCurrentPathMatches = (
  pathToCompareWith: string,
  end = false
): boolean => {
  const currentPath = window.location.pathname
  const hasMatch = !!matchPath(
    {
      path: pathToCompareWith,
      end
    },
    currentPath
  )

  return hasMatch
}

export const checkIsConfirmationPage = (): boolean => {
  return checkIfCurrentPathMatches(
    '/project/:projectId/survey/:surveyId/confirm'
  )
}

export const checkIsResultsPage: () => boolean = () => {
  return checkIfCurrentPathMatches(
    '/project/:projectId/survey/:surveyId/results'
  )
}

export const checkIsProjectsPage = (): boolean => {
  return checkIfCurrentPathMatches('/projects')
}

export const checkIsAudiencePage = (): boolean => {
  return checkIfCurrentPathMatches(
    '/project/:projectId/survey/:surveyId/audience'
  )
}

export const checkIsFDChatResultsPage = (): boolean => {
  return checkIfCurrentPathMatches(
    '/project/:projectId/fdchat/:surveyId/results'
  )
}

// The shared questionnaire editor is mounted project-scoped by the
// multi-market flow and wave-scoped by a tracker's flow. Both are the master
// survey page: it drives header offsets and hides the single-market bottom nav,
// so missing the wave path misaligns the layout and offers the wrong actions.
export const isMasterSurveyPage = (): boolean => {
  return (
    checkIfCurrentPathMatches('/project/:projectId/survey', true) ||
    checkIfCurrentPathMatches('/project/:projectId/wave/:waveId/survey', true)
  )
}

export const checkIsQuestionnairePage = (): boolean => {
  return checkIfCurrentPathMatches(
    '/project/:projectId/survey/:surveyId/questionnaire'
  )
}

export const checkIsTranslationsPage = (): boolean => {
  return checkIfCurrentPathMatches(
    '/project/:projectId/survey/:surveyId/translations'
  )
}

// Same check under a second name; both are used across the questionnaire.
export const checkIfMasterSurveyPage = (): boolean => isMasterSurveyPage()

export const getMinutesSuffix = (lengthOfInterview: number): string => {
  if (lengthOfInterview === 1) {
    return 'minute'
  }
  return 'minutes'
}

export const getDaysSuffix = (deliveryDays: number): string => {
  if (deliveryDays && deliveryDays <= 1) {
    return 'day'
  }
  return 'days'
}

// Resolve a language code (e.g. "fr", "nl") to its English display name
// (e.g. "French", "Dutch") using the platform's built-in Intl language data,
// so we don't hand-maintain a language table. Created once at module load;
// `of` returns the canonicalised code when it has no name for it, and we
// fall back to the raw code if Intl rejects the input as malformed.
const languageDisplayNames = new Intl.DisplayNames(['en'], {
  type: 'language'
})

export const getLanguageLabel = (code: string): string => {
  try {
    return languageDisplayNames.of(code) ?? code
  } catch {
    return code
  }
}

export const numberToFixedString = (myNumber: number): string => {
  if (Number.isSafeInteger(myNumber)) {
    return myNumber.toFixed(0)
  }

  return myNumber.toFixed(1)
}

export const getPercentageFromFloat: (
  float: number | undefined
) => number | undefined = (float) => {
  if (float) {
    return float * 100
  }
  return undefined
}

export const isQuotaValueZeroError: (
  validationErrors: CustomCriterionValidationErrors | undefined,
  responseOptionLk: string
) => boolean | undefined = (validationErrors, responseOptionLk) => {
  return (
    validationErrors?.customCriterionOptionsErrors
      .find(
        (customCriterionOptionsError) =>
          customCriterionOptionsError.responseOptionLk === responseOptionLk
      ) // @todo Legacy eslint violation – fix this when editing
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ?.errors?.includes(CustomOptionValidationError.CustomQuotaIsZeroError)
  )
}

export const getEnvironment: () =>
  | 'development'
  | 'PR'
  | 'staging'
  | 'production' = () => {
  if (
    process.env.NODE_ENV === 'production' &&
    window.location.hostname === 'dashboard.focaldata.com'
  ) {
    return 'production'
  }
  if (
    process.env.NODE_ENV === 'production' &&
    window.location.hostname.includes('focaldata.dev')
  ) {
    return 'staging'
  }
  if (
    process.env.NODE_ENV === 'production' &&
    window.location.hostname.includes('cin-fd-surveys-pr')
  ) {
    return 'PR'
  }
  return 'development'
}

export const getPage: () =>
  | 'projectsPage'
  | 'audiencePage'
  | 'questionnairePage'
  | 'confirmPage'
  | 'resultsPage'
  | 'unknownPage' = () => {
  if (checkIsProjectsPage()) {
    return 'projectsPage'
  }
  if (checkIsAudiencePage()) {
    return 'audiencePage'
  }
  if (checkIsQuestionnairePage()) {
    return 'questionnairePage'
  }
  if (checkIsConfirmationPage()) {
    return 'confirmPage'
  }
  if (checkIsResultsPage()) {
    return 'resultsPage'
  }
  return 'unknownPage'
}

export const captureApolloError: (
  errorType: LoggerErrorType,
  mutationName: string,
  apolloError: ApolloError
) => void = (errorType, mutationName, apolloError) => {
  const page = getPage()
  const errorMessage = `[${mutationName}] ${errorType}_${page} ${apolloError.message}`
  if (getEnvironment() !== 'production') {
    console.error(errorMessage, apolloError)
  }
  datadogLogs.logger.error(errorMessage, apolloError)
}

export const getResultsPath: (surveyId: string) => string = (surveyId) =>
  `/survey/${surveyId}/results`

export const validateSurveyPath = (): boolean => {
  return [
    '/project/:projectId/survey/:surveyId/audience',
    '/project/:projectId/survey/:surveyId/questionnaire',
    '/project/:projectId/survey/:surveyId/translations',
    '/project/:projectId/survey/:surveyId/confirm',
    '/project/:projectId/survey/:surveyId/results'
  ].some((path) => checkIfCurrentPathMatches(path))
}

export const MATRIX_QUESTION_ID_DELIMITER = '__'
export const composeSelectedQuestionLk: (
  questionLk: string,
  matrixTitleLk: string | undefined
) => string = (questionLk, matrixTitleLk) =>
  `${questionLk}${MATRIX_QUESTION_ID_DELIMITER}${matrixTitleLk}`

export const getIdsFromSelectedQuestionLk: (
  selectedQuestionLk: string
) => [string, string | undefined] = (selectedQuestionLk) => {
  const [questionLk, matrixTitleLk] = selectedQuestionLk.split(
    MATRIX_QUESTION_ID_DELIMITER
  )

  return [questionLk, matrixTitleLk]
}

/**
 * Allows to safely get values by non-existent keys in a wrapped object
 * @example
 * const o1 = safeObject({}, 42)
 * o1.nonExistentKey // 42
 * const o2 = safeObject(undefined, '')
 * o2.nonExistentKey // ''
 */
export const safeObject = <T>(
  object: Record<string, T> = {},
  defaultValue?: T
): Record<string, T> => {
  return new Proxy(object, {
    get(object, key: string) {
      return key in object ? object[key] : defaultValue
    }
  })
}

export const truncate = (str: string, numbOfCharacters: number) => {
  return str.length > numbOfCharacters
    ? `${str.slice(0, numbOfCharacters - 1)}...`
    : str
}

export const isURL = (url: string) => url.includes('http')

/**
 * Generates a unique id that increments each time this function is called
 *
 */
export const uniqueId = (() => {
  let counter = 0

  return (str = '') => {
    counter += 1
    return `${str}${counter}`
  }
})()
